/**
 * OracleConnectionPanel — capture OAuth 2.0 client-credentials config for
 * Oracle Fusion Cloud ERP. Actual token exchange ships in a later release —
 * this saves the creds securely + shows "credentials saved · live sync
 * pending Q2 2026" until then.
 */
import React, { useEffect, useState } from "react";
import { Cloud, Loader2, Trash2, Check, AlertCircle, Save } from "lucide-react";
import client from "../../../lib/apiClient";

export default function OracleConnectionPanel() {
  const [cfg, setCfg] = useState(null);
  const [form, setForm] = useState({ base_url: "", client_id: "", client_secret: "", scope: "urn:opc:idm:__myscopes__" });
  const [busy, setBusy] = useState(false);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [saved, setSaved] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await client.get("/connections/oracle");
      setCfg(data);
      if (data?.configured) {
        setForm(f => ({ ...f, base_url: data.base_url || "", client_id: data.client_id || "", scope: data.scope || f.scope }));
      }
    } catch (e) {
      setErr(e?.response?.data?.detail || "Failed to load Oracle config");
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const save = async (e) => {
    e.preventDefault();
    setBusy(true); setErr(""); setSaved(false);
    try {
      await client.post("/connections/oracle", form);
      setSaved(true);
      await load();
    } catch (e) {
      setErr(e?.response?.data?.detail || "Save failed");
    } finally { setBusy(false); }
  };

  const clear = async () => {
    if (!window.confirm("Remove Oracle Fusion credentials?")) return;
    setBusy(true);
    try {
      await client.delete("/connections/oracle");
      setForm({ base_url: "", client_id: "", client_secret: "", scope: "urn:opc:idm:__myscopes__" });
      await load();
    } catch (e) {
      setErr(e?.response?.data?.detail || "Clear failed");
    } finally { setBusy(false); }
  };

  return (
    <div className="p-5 rounded-md border" style={{ background: "#FFFFFF", borderColor: "#F0D3D8" }}
         data-testid="oracle-connection-panel">
      <div className="flex items-center gap-2 mb-3">
        <span className="w-8 h-8 rounded-sm grid place-items-center text-white"
              style={{ background: "linear-gradient(135deg, #7A1F2B 0%, #DC2626 100%)" }}>
          <Cloud size={14}/>
        </span>
        <div className="flex-1">
          <div className="font-display text-base font-extrabold text-[#0F1723]">Oracle Cloud ERP · Connection</div>
          <div className="font-mono text-[10px] uppercase tracking-widest text-zinc-500">
            OAuth 2.0 client credentials · Financials · SCM · HCM
          </div>
        </div>
        {cfg?.configured && (
          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-mono font-semibold"
                style={{ background: "#DCFCE7", color: "#166534" }}>
            <Check size={10}/> Credentials saved
          </span>
        )}
      </div>

      {loading ? <div className="p-3 text-center"><Loader2 size={14} className="animate-spin text-zinc-400 inline"/></div> : (
        <>
          {cfg?.note && (
            <div className="p-2.5 rounded-sm mb-4 text-[12px] flex items-start gap-2"
                 style={{ background: "#FEF3F2", border: "1px solid #FCA5A5" }}>
              <AlertCircle size={13} className="text-red-700 shrink-0 mt-0.5"/>
              <span className="text-red-800 leading-relaxed">{cfg.note}</span>
            </div>
          )}

          <form onSubmit={save} className="grid md:grid-cols-2 gap-3">
            <div className="md:col-span-2">
              <label className="font-mono text-[10px] uppercase tracking-widest text-zinc-500">Base URL</label>
              <input required value={form.base_url} onChange={e => setForm(f => ({...f, base_url: e.target.value}))}
                     className="fld w-full mt-1"
                     placeholder="https://<pod>.fa.<region>.oraclecloud.com"
                     data-testid="oracle-base-url"/>
            </div>
            <div>
              <label className="font-mono text-[10px] uppercase tracking-widest text-zinc-500">Client ID</label>
              <input required value={form.client_id} onChange={e => setForm(f => ({...f, client_id: e.target.value}))}
                     className="fld w-full mt-1" data-testid="oracle-client-id"/>
            </div>
            <div>
              <label className="font-mono text-[10px] uppercase tracking-widest text-zinc-500">Client Secret</label>
              <input required type="password" value={form.client_secret} onChange={e => setForm(f => ({...f, client_secret: e.target.value}))}
                     className="fld w-full mt-1"
                     placeholder={cfg?.client_secret_masked || "•••••••"}
                     data-testid="oracle-client-secret"/>
            </div>
            <div className="md:col-span-2">
              <label className="font-mono text-[10px] uppercase tracking-widest text-zinc-500">Scope</label>
              <input value={form.scope} onChange={e => setForm(f => ({...f, scope: e.target.value}))}
                     className="fld w-full mt-1" data-testid="oracle-scope"/>
            </div>

            {err && <div className="md:col-span-2 p-2.5 rounded-sm bg-red-50 border border-red-200 text-red-800 text-sm" data-testid="oracle-error">{err}</div>}
            {saved && <div className="md:col-span-2 p-2.5 rounded-sm bg-emerald-50 border border-emerald-200 text-emerald-800 text-sm" data-testid="oracle-saved">Credentials saved securely. We'll email you when your tenant is on-boarded for live sync.</div>}

            <div className="md:col-span-2 flex items-center gap-2 flex-wrap">
              <button type="submit" className="btn btn-primary" disabled={busy} data-testid="btn-save-oracle">
                {busy ? <Loader2 size={13} className="animate-spin"/> : <Save size={13}/>} Save credentials
              </button>
              {cfg?.configured && (
                <button type="button" className="btn btn-ghost text-red-600" onClick={clear} disabled={busy} data-testid="btn-clear-oracle">
                  <Trash2 size={13}/> Remove credentials
                </button>
              )}
            </div>
          </form>
        </>
      )}
    </div>
  );
}
